import { existsSync, readFileSync } from "node:fs";
import { mkdir, readFile, writeFile } from "node:fs/promises";
import { join } from "node:path";
import { packageRoot } from "./render/paths.js";

const PKG_NAME = "@srinivasa314/ovid";

function ownVersion(): string {
  try {
    return JSON.parse(readFileSync(join(packageRoot(), "package.json"), "utf8")).version as string;
  } catch {
    return "latest";
  }
}

/**
 * Ensure the project has ovid as a devDependency (creating package.json if
 * absent). ovid runs via the project-local CLI, so it must be installed locally.
 * Returns true if the file was created/changed and `npm install` is needed.
 */
async function ensureDevDependency(projectRoot: string): Promise<boolean> {
  const pkgPath = join(projectRoot, "package.json");
  const version = `^${ownVersion()}`;
  let pkg: Record<string, any> = {};
  if (existsSync(pkgPath)) {
    try {
      pkg = JSON.parse(readFileSync(pkgPath, "utf8"));
    } catch {
      return false; // don't clobber an unparseable package.json
    }
  }
  pkg.devDependencies ??= {};
  if (pkg.devDependencies[PKG_NAME]) return false; // already present
  pkg.name ??= "ovid-project";
  pkg.version ??= "0.0.0";
  pkg.private ??= true;
  pkg.devDependencies[PKG_NAME] = version;
  await writeFile(pkgPath, JSON.stringify(pkg, null, 2) + "\n", "utf8");
  return true;
}

const CONFIG_TEMPLATE = `import { defineConfig } from "@srinivasa314/ovid";

// All fields optional; values shown are the defaults.
export default defineConfig({
  // specDir: "ovid",
  // viewport: { width: 1440, height: 900 },
  // video: { width: 1280, height: 720, fps: 30 },
  // slowMo: 800,
  // pacing: { terminalDwell: 2.5, browserEndHold: 1.2 },
});
`;

const GUIDE = `# Writing ovid e2e tests

ovid records a polished video (terminal + browser) of a test **and** asserts behavior in code.
Specs live in \`ovid/*.spec.ts\` and run with \`npx ovid test\`. Assertions decide pass/fail; the video is for humans.

## Skeleton
\`\`\`ts
import { test, expect } from "@srinivasa314/ovid/test";

test("user can save a note", async ({ ovid }) => {
  // ovid.terminal(...) and ovid.browser(...) steps
});
\`\`\`

## ovid.terminal(command, opts?)
Runs \`command\` in a shell.
- \`name?\` — named long-lived terminal (its own shell). **Give distinct names when a test uses more than one terminal** (titlebar shows \`Terminal: <name>\`; unnamed → \`Terminal\`). Reuse a name to run more commands in that shell.
- \`waitFor?: RegExp\` — **server mode**: resolves once output matches, leaving the process running (e.g. a dev server). A foreground server occupies its terminal — use a different \`name\` for other commands.
- \`expect?: RegExp\` — output must match.
- \`not?: RegExp\` — output must NOT match.
- \`exitCode?: number\` — required exit code (defaults to 0 when no other assertion).
- \`caption?\` — caption shown under the step.
- \`timeout?\` — ms (default 30000).

## ovid.browser(caption, async (page) => { ... }, opts?)
Drives a Playwright \`page\` (full Playwright API). \`caption\` is shown under the step.
Titlebar shows the page URL by default; override with \`opts.title\`. Assert with Playwright \`expect\`.

## Example (multi-terminal + browser)
\`\`\`ts
import { test, expect } from "@srinivasa314/ovid/test";

test("note persists", async ({ ovid }) => {
  await ovid.terminal("flask --app api/app.py run -p 3001", { name: "API", waitFor: /Running on/, caption: "Start the API" });
  await ovid.terminal("python3 -m http.server 3000 --directory web", { name: "Web", waitFor: /Serving HTTP/, caption: "Serve the UI" });

  await ovid.browser("Create a note", async (page) => {
    await page.goto("http://localhost:3000");
    await page.getByPlaceholder("New note").fill("Buy milk");
    await page.getByRole("button", { name: "Save" }).click();
    await expect(page.getByText("Buy milk")).toBeVisible();
  });

  await ovid.terminal('sqlite3 api/notes.db "select count(*)"', { name: "DB", expect: /1/, caption: "Persisted to SQLite" });
});
\`\`\`

## Rules of thumb
- Commands run from the project root (cwd of \`npx ovid test\`); use paths relative to it.
- Servers are killed automatically when the test ends.
- Each terminal/browser block becomes one video segment — keep them focused.
- Run all specs with \`npx ovid test\`, or one with \`npx ovid test <name-substring>\`.
- Passing runs save raw artifacts but don't render a video (kept cheap); run \`npx ovid render <name-substring>\` to produce \`final.mp4\`/\`.gif\` for one.
`;

async function writeIfAbsent(path: string, content: string): Promise<"created" | "exists"> {
  if (existsSync(path)) return "exists";
  await writeFile(path, content, "utf8");
  return "created";
}

// --- Claude Code integration (.claude/settings.json hooks + skill) ---

const OVID_HOOK_MARKER = "ovid claude-hook";
const OVID_PERMISSION = "Bash(npx ovid:*)";

// SessionStart: inject the workflow pointer on start/resume/clear/compact.
const SESSION_START_GROUP = {
  matcher: "startup|resume|clear|compact",
  hooks: [{ type: "command", command: "npx ovid claude-hook session-start" }],
};
// PreToolUse: gate to gh commands (fast; `if` degrades safely on older Claude
// Code — the hook then runs on all Bash calls and the script's own regex still
// only denies `gh pr create`).
const PRE_TOOL_USE_GROUP = {
  matcher: "Bash",
  hooks: [{ type: "command", if: "Bash(gh *)", command: "npx ovid claude-hook pre-tool-use" }],
};

function groupHasOvid(group: unknown): boolean {
  const hooks = (group as { hooks?: unknown })?.hooks;
  return (
    Array.isArray(hooks) &&
    hooks.some((h) => typeof (h as { command?: unknown })?.command === "string" && (h as { command: string }).command.includes(OVID_HOOK_MARKER))
  );
}

/**
 * Merge ovid's hooks + permission into an existing Claude Code settings object,
 * preserving everything else. Idempotent: prior ovid hook groups are replaced
 * (so a re-init picks up updated matchers/commands) and the permission isn't
 * duplicated.
 */
export function mergeClaudeSettings(existing: Record<string, any>): Record<string, any> {
  const s: Record<string, any> = existing && typeof existing === "object" ? { ...existing } : {};

  s.permissions = { ...(s.permissions ?? {}) };
  const allow: unknown[] = Array.isArray(s.permissions.allow) ? [...s.permissions.allow] : [];
  if (!allow.includes(OVID_PERMISSION)) allow.push(OVID_PERMISSION);
  s.permissions.allow = allow;

  s.hooks = { ...(s.hooks ?? {}) };
  const keepGroups = (arr: unknown): unknown[] => (Array.isArray(arr) ? arr.filter((g) => !groupHasOvid(g)) : []);
  s.hooks.SessionStart = [...keepGroups(s.hooks.SessionStart), SESSION_START_GROUP];
  s.hooks.PreToolUse = [...keepGroups(s.hooks.PreToolUse), PRE_TOOL_USE_GROUP];

  return s;
}

/** Write/merge .claude config for Claude Code. Returns per-item status lines. */
async function scaffoldClaude(projectRoot: string): Promise<Array<[string, string]>> {
  const items: Array<[string, string]> = [];

  await mkdir(join(projectRoot, ".claude", "skills", "ovid"), { recursive: true });
  const skill = await readFile(join(packageRoot(), "assets", "claude-code", "skill.md"), "utf8");
  items.push([".claude/skills/ovid/SKILL.md", await writeIfAbsent(join(projectRoot, ".claude", "skills", "ovid", "SKILL.md"), skill)]);

  const settingsPath = join(projectRoot, ".claude", "settings.json");
  let existing: Record<string, any> = {};
  let existed = false;
  if (existsSync(settingsPath)) {
    existed = true;
    try {
      existing = JSON.parse(readFileSync(settingsPath, "utf8"));
    } catch {
      items.push([".claude/settings.json", "unparseable — left untouched; add the ovid hooks manually"]);
      return items;
    }
  }
  await writeFile(settingsPath, JSON.stringify(mergeClaudeSettings(existing), null, 2) + "\n", "utf8");
  items.push([".claude/settings.json", existed ? "merged" : "created"]);
  return items;
}

export type Agent = "pi" | "claude";

/** Scaffold config + guide + gitignore + the selected agent integrations. */
export async function init(projectRoot: string, opts: { agents?: Agent[] } = {}): Promise<void> {
  const agents = opts.agents && opts.agents.length ? opts.agents : (["pi", "claude"] as Agent[]);

  await mkdir(join(projectRoot, "ovid"), { recursive: true });
  await mkdir(join(projectRoot, ".ovid"), { recursive: true });

  const items: Array<[string, string]> = [
    ["ovid.config.ts", await writeIfAbsent(join(projectRoot, "ovid.config.ts"), CONFIG_TEMPLATE)],
    ["ovid/WRITING-OVID-E2E-TESTS.md", await writeIfAbsent(join(projectRoot, "ovid", "WRITING-OVID-E2E-TESTS.md"), GUIDE)],
    [".ovid/.gitignore", await writeIfAbsent(join(projectRoot, ".ovid", ".gitignore"), "*\n")],
  ];

  if (agents.includes("pi")) {
    await mkdir(join(projectRoot, ".pi", "extensions"), { recursive: true });
    const extension = await readFile(join(packageRoot(), "assets", "pi-extension.ts"), "utf8");
    items.push([".pi/extensions/ovid.ts", await writeIfAbsent(join(projectRoot, ".pi", "extensions", "ovid.ts"), extension)]);
  }
  if (agents.includes("claude")) {
    items.push(...(await scaffoldClaude(projectRoot)));
  }

  const needsInstall = await ensureDevDependency(projectRoot);
  const tagFor = (status: string) =>
    status === "created" ? "+ created" : status === "exists" ? "= exists " : status === "merged" ? "~ merged " : "! " + status;
  for (const [name, status] of items) console.log(`  ${tagFor(status)} ${name}`);
  if (needsInstall) console.log(`  + added ${PKG_NAME} to package.json devDependencies`);

  const lines = [
    needsInstall ? "\nNext: run `npm install` to install ovid locally, then" : "\nNext:",
    " add a spec in ./ovid/*.spec.ts (see ovid/WRITING-OVID-E2E-TESTS.md), then run `npx ovid test`.",
  ];
  if (agents.includes("pi")) lines.push("\npi auto-discovers the ovid_test tool from .pi/extensions/ovid.ts (trust the project once).");
  if (agents.includes("claude"))
    lines.push(
      "\nClaude Code: the ovid skill + hooks are in .claude/ — approve them on the first `claude` run in this project" +
        " (headless `-p` needs that trust already granted).",
    );
  console.log(lines.join(""));
}
